import {
  Typography,
  PageLayout,
  EmptyContainer,
  TYPOGRAPHY_SIZE,
  VerticalCollapseBar,
  EMPTY_ILLUSTRATIONS,
  PLATFORM_THEME_COLORS,
  Loader,
} from '@thinknet-gmbh/platform-common-ui';
import { useTranslation } from 'react-i18next';
import React, { useEffect, useState } from 'react';
import { useResizeDetector } from 'react-resize-detector';
import { GanttAccessRightsEnum } from '@thinknet-gmbh/platform-common';

import { useCurrentMemberContext } from 'src/contexts';
import { useGetTimeStripes } from 'src/hooks/time-stripes/get-time-stripes.hook';
import { useGetAllEmployeesForActiveGantt } from 'src/hooks/employees/get-employees.hook';
import { useGetCurrentMemberAccount } from 'src/hooks/accounts/get-current-member-account.hook';
import { useGetAbsenceReasonForGantt } from 'src/hooks/absence-reasons/get-absence-reasons-by-company.hook';
import { useGetAllWorkingHoursByCurrentGantt } from 'src/hooks/employees/get-gantt-employees-working-hours.hook';

import {
  GanttContainer,
  HeaderContainer,
  GanttPageLayout,
  CalendarViewPool,
  GanttPageContainer,
  HeaderGanttContainer,
  GanttCalendarContainer,
} from './gantt.styles';
import { GanttViewsEnum } from './gantt.enums';
import { GanttCalendar } from './gantt-calendar/';
import { GANTT_CALENDAR_CONTAINER_ID, GANTT_CONTROL_DEFAULT } from './gantt.constants';
import { buildViewRange, getSortedEmployees } from './gantt.helpers';
import { IActiveViewRange, IGanttControl } from './gantt.interfaces';
import { GanttTopBarButtons } from './components/gantt-top-bar-buttons/';

const Gantt: React.FC<{ fontIsLoaded: boolean }> = ({ fontIsLoaded }) => {
  const { t, i18n } = useTranslation(['common', 'gantt', 'employee', 'projects', 'customers']);

  const [search, setSearch] = useState('');
  const { ganttId, ganttsAccess = [] } = useCurrentMemberContext();
  const { member } = useGetCurrentMemberAccount();
  const [ganttControl, setGanttControl] = useState<IGanttControl>({
    ...GANTT_CONTROL_DEFAULT,
    scrollToDate: new Date(),
  });
  const [activeViewRanges, setActiveViewRanges] = useState<IActiveViewRange[]>([
    buildViewRange({ i18n }),
  ]);
  const {
    loading: timStripesLoading,
    timeStripes,
    onTableChange,
    paginationConfig,
  } = useGetTimeStripes(search);
  const { employees, loading: employeeLoading } = useGetAllEmployeesForActiveGantt();
  const sortedEmployees = getSortedEmployees(employees, member?.gantt?.employeeGanttOrderIds);
  const { absenceReasons, loading: absenceReasonsLoading } = useGetAbsenceReasonForGantt();
  const { employeesWorkingHours, loading: employeesWorkingHoursLoading } =
    useGetAllWorkingHoursByCurrentGantt();
  const { width, ref } = useResizeDetector();
  const loading =
    timStripesLoading || employeeLoading || absenceReasonsLoading || employeesWorkingHoursLoading;
  const windowWidth = window.innerWidth;
  const isThreeMonthView = ganttControl.view === GanttViewsEnum.THREE_MONTHS;

  useEffect(() => {
    const { accessRight } = ganttsAccess.find(access => access.ganttId === ganttId) || {};

    setGanttControl(control => ({ ...control, access: accessRight || GanttAccessRightsEnum.VIEW }));
  }, [ganttId, ganttsAccess]);

  useEffect(() => {
    const newRanges = [];
    const isYearView = ganttControl.view === GanttViewsEnum.YEAR;

    // if month or months view set first month
    if (!isYearView) newRanges.push(buildViewRange({ i18n }));

    // if months add two more month to view
    if (ganttControl.view === GanttViewsEnum.THREE_MONTHS) {
      [1, 2].forEach(increment => newRanges.push(buildViewRange({ i18n, increment })));
    }

    // if year view push year range
    if (isYearView) newRanges.push(buildViewRange({ t, i18n, type: 'year' }));

    setActiveViewRanges(newRanges);
  }, [ganttControl.view, t, i18n]);

  const onNavigationClick = (increment: number) => () =>
    // map each active range view and build incremented or decremented view
    setActiveViewRanges(ranges =>
      ranges.map(({ startDate, type }) =>
        buildViewRange({ t, i18n, date: startDate, type, increment })
      )
    );

  const onTimeStripeChangeSearch = ({ target: { value } }: any) => setSearch(value);

  const onTodayClick = () => {
    const today = new Date();
    const newRanges: any = [];

    if (isThreeMonthView) {
      [0, 1, 2].forEach(increment =>
        newRanges.push(buildViewRange({ t, i18n, date: today, increment }))
      );
    }

    if (ganttControl.view === GanttViewsEnum.YEAR) {
      newRanges.push(buildViewRange({ t, i18n, date: today, type: 'year' }));
    }

    if (ganttControl.view === GanttViewsEnum.MONTH) {
      newRanges.push(buildViewRange({ t, i18n, date: today }));
    }

    setActiveViewRanges(newRanges);

    setGanttControl(control => ({ ...control, scrollToDate: today }));
  };

  const onFullScreenClickBtn = () => {
    setGanttControl(control => ({ ...control, fullScreenActive: !control.fullScreenActive }));
  };

  const topBarProps = {
    ganttControl,
    setGanttControl,
    onTodayClickBtn: onTodayClick,
    onClickNextBtn: onNavigationClick(1),
    onClickPrevBtn: onNavigationClick(-1),
    onFullScreenClickBtn,
  };

  if (!ganttId || !sortedEmployees.length) {
    return (
      <PageLayout pageTitle={t('common:labels.gantt')}>
        {loading ? (
          <Loader />
        ) : (
          <EmptyContainer
            emptyTitle={
              ganttId ? t('gantt:messages.emptyTitle') : t('gantt:messages.ganttNotSelectedTitle')
            }
            illustration={EMPTY_ILLUSTRATIONS.dataAnalysis}
            contentText={
              ganttId ? t('gantt:messages.emptyText') : t('gantt:messages.ganttNotSelectedText')
            }
          />
        )}
      </PageLayout>
    );
  }

  return (
    <GanttPageLayout
      pageTitle={t('common:labels.gantt')}
      contentFullScreenActive={ganttControl.fullScreenActive}
      toggleContentFullScreen={fullScreenActive =>
        setGanttControl(control => ({ ...control, fullScreenActive }))
      }
      sidebarOptions={{
        t,
        type: 'table',
        onChangeSearch: onTimeStripeChangeSearch,

        tableProps: {
          loading,
          onChange: onTableChange,
          dataSource: timeStripes,
          pagination: paginationConfig,
        },
        actionsAllowed: ganttControl.access === GanttAccessRightsEnum.EDIT,
      }}
      topBarElement={
        ganttControl.view === GanttViewsEnum.THREE_MONTHS && <GanttTopBarButtons {...topBarProps} />
      }
    >
      <GanttPageContainer
        ref={ref}
        id={GANTT_CALENDAR_CONTAINER_ID}
        isFullScreenForThreeMonthView={ganttControl.fullScreenActive && isThreeMonthView}
      >
        {!isThreeMonthView ? (
          <>
            <HeaderContainer>
              <Typography weight="bold" size={TYPOGRAPHY_SIZE.medium}>
                {activeViewRanges[0].dateTitle}
              </Typography>
              <GanttTopBarButtons {...topBarProps} />
            </HeaderContainer>

            <GanttContainer>
              <GanttCalendarContainer style={{ width }}>
                <GanttCalendar
                  containerWidth={ganttControl.fullScreenActive ? windowWidth : width || 0}
                  employees={sortedEmployees}
                  fontIsLoaded={fontIsLoaded}
                  ganttControl={ganttControl}
                  viewRange={activeViewRanges[0]}
                  absenceReasons={absenceReasons}
                  employeesWorkingHours={employeesWorkingHours}
                  colorSchema={member?.gantt?.colorSchema}
                />
              </GanttCalendarContainer>
            </GanttContainer>
          </>
        ) : (
          <>
            {ganttControl.fullScreenActive && <GanttTopBarButtons {...topBarProps} />}
            <CalendarViewPool>
              {activeViewRanges.map(viewRange => (
                <VerticalCollapseBar
                  collapsedByDefault
                  key={viewRange.dateTitle}
                  titleTextOptions={{
                    text: viewRange.dateTitle,
                    weight: 'bold',
                    size: TYPOGRAPHY_SIZE.medium,
                    fontColor: PLATFORM_THEME_COLORS.primary.black,
                  }}
                >
                  <HeaderGanttContainer>
                    <Typography weight="bold" size={TYPOGRAPHY_SIZE.medium}>
                      {viewRange.dateTitle}
                    </Typography>
                  </HeaderGanttContainer>

                  <GanttCalendar
                    containerWidth={ganttControl.fullScreenActive ? windowWidth : width || 0}
                    employees={sortedEmployees}
                    viewRange={viewRange}
                    fontIsLoaded={fontIsLoaded}
                    ganttControl={ganttControl}
                    absenceReasons={absenceReasons}
                    employeesWorkingHours={employeesWorkingHours}
                    colorSchema={member?.gantt?.colorSchema}
                  />
                </VerticalCollapseBar>
              ))}
            </CalendarViewPool>
          </>
        )}
      </GanttPageContainer>
    </GanttPageLayout>
  );
};

export default Gantt;
